import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, MouseEvent } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import Checkbox from '@/components/Forms/Checkbox';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import useCompaniesOptions from '@/hooks/useCompaniesOptions';
import { DepartmentResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormData = {
  name: string;
  short: string;
  company_ids: number[];
  hide_from_dropdowns: boolean;
};

interface Props {
  department?: DepartmentResource;
  onClose: () => void;
}

const DepartmentFormModal: FC<Props> = ({ department, onClose }) => {
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const { data: companyOptions = [] } = useCompaniesOptions();

  // Keep hidden companies the department already belongs to, otherwise they'd
  // silently drop out of the selection.
  const visibleCompanyOptions = companyOptions.filter(
    (company) =>
      !company.hide_from_dropdowns ||
      !!department?.companies.some((c) => c.id === company.value),
  );

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      name: department?.name || '',
      short: department?.short || '',
      company_ids: department?.companies.map((c) => c.id) ?? [],
      hide_from_dropdowns: department?.hide_from_dropdowns || false,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (formData) => {
      // Departments predate the company link, so every save now has to set at
      // least one.
      if (!formData.company_ids.length) {
        setError('company_ids', {
          message: 'At least one company is required',
        });
        return;
      }

      try {
        const data = {
          name: formData.name,
          short: formData.short,
          company_ids: formData.company_ids,
          hide_from_dropdowns: formData.hide_from_dropdowns,
        };

        if (department) {
          await api.put<typeof data, DepartmentResource>(
            `/v1/departments/${department.id}`,
            data,
          );
        } else {
          await api.post<typeof data, DepartmentResource>(
            '/v1/departments',
            data,
          );
        }

        await queryClient.invalidateQueries(['departments_and_units'], {
          exact: false,
        });

        onClose();
        alerts.success(
          `Department was successfully ${department ? 'updated' : 'created'}`,
        );
      } catch (error) {
        handleFormErrors<FormData>(error, setError, alerts);
      }
    })(e);
  };

  const onCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <Modal>
      <SidePanel
        heading={`${department ? 'Edit' : 'Add'} Department`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormData>
                control={control}
                name="name"
                label="Name"
                id="nameInput"
                placeholder="Add department name"
              />
              <FormField<FormData>
                control={control}
                name="short"
                label="Short"
                id="shortInput"
                placeholder="Add short code"
              />
              <FormField<FormData, number>
                control={control}
                name="company_ids"
                label="Companies"
                id="companySelect"
                type="multiselect"
                placeholder="Add company"
                options={visibleCompanyOptions}
              />
              <Larger>
                <Checkbox control={control} name="hide_from_dropdowns">
                  Hide from dropdowns
                </Checkbox>
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {department ? 'Update' : 'Add'}
            </Button>
            <Button color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default DepartmentFormModal;
